/** Homepage — full hero with visual right-column, pillars, stats strip, trust strip.
 *  Structurally follows blueprint/Nikhil Vatika Lab.html. Featured-content rails will
 *  arrive in feature prompts (Courses, Sadhana, Products) — placeholders shown here.
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Flame,
  BookOpen,
  FlaskConical,
  Compass,
  Gem,
  LayoutGrid,
  Atom,
  Globe2,
  ShieldCheck,
  Award,
  Download,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, Lede, AccentItalic, Button, ArtCard, Avatar } from "../components/ui";
import { api } from "../lib/api";
import { useRevealOnScroll } from "../lib/useRevealOnScroll";
import { HomeVideos } from "../features/videos/Videos";

const PILLARS = [
  { label: "Courses", to: "/courses", Icon: LayoutGrid },
  { label: "Sadhana", to: "/sadhana", Icon: Flame },
  { label: "Library", to: "/library", Icon: BookOpen },
  { label: "Research", to: "/research", Icon: FlaskConical },
  { label: "Services", to: "/services", Icon: Compass },
  { label: "Products", to: "/products", Icon: Gem },
];

const STATS = [
  { n: "100+", l: "Spiritual Courses" },
  { n: "40K+", l: "Library Volumes" },
  { n: "50+", l: "Research Papers" },
  { n: "60+", l: "Countries Reached" },
];

const TRUST = [
  { Icon: BookOpen, t: "Spiritual Library", s: "40,000+ volumes" },
  { Icon: Flame, t: "Sadhana Tracker", s: "Daily practice" },
  { Icon: Atom, t: "Research Center", s: "Peer-reviewed" },
  { Icon: Globe2, t: "Global Community", s: "60+ countries" },
  { Icon: ShieldCheck, t: "Verified Certificates", s: "On completion" },
];

export function HomePage() {
  useRevealOnScroll();

  return (
    <>
      {/* HERO */}
      <Section padded="none" className="pt-6 pb-12 lg:pb-16" data-testid="home-hero">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
            {/* COPY */}
            <div>
              <Eyebrow className="reveal">
                A <b className="text-text font-semibold">World&apos;s Biggest Spiritual University</b>
              </Eyebrow>

              <DisplayHeading className="mt-6 reveal text-[clamp(36px,5vw,56px)] xl:text-[clamp(44px,3.6vw,58px)]" data-delay="1">
                Where Ancient Wisdom
                <br />
                Meets <AccentItalic>Future Technology</AccentItalic>
              </DisplayHeading>

              <Lede className="mt-7 reveal" data-delay="2">
                Nikhil Vatika Lab unites authentic lineage teaching, advanced sadhana, a
                40,000-volume digital library and a dedicated consciousness-research center — one
                institution for the serious study and practice of the spiritual sciences.
              </Lede>

              {/* Pillars */}
              <div
                className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-2.5 reveal"
                data-testid="home-pillars"
                data-delay="3"
              >
                {PILLARS.map(({ label, to, Icon }) => (
                  <Link
                    key={label}
                    to={to}
                    data-testid={`pillar-${label.toLowerCase()}`}
                    className="group flex items-center gap-3 px-4 py-3 rounded-2xl border border-line-2 bg-surface hover:border-mauve hover:bg-surface-2 transition-all duration-300 ease-nvl hover:-translate-y-[1px]"
                  >
                    <Icon size={17} className="text-mauve" />
                    <span className="text-[14px] font-medium">{label}</span>
                  </Link>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-9 flex flex-wrap items-center gap-3 reveal" data-delay="4">
                <Button asChild data-testid="cta-start-learning">
                  <Link to="/courses">
                    Start Learning <ArrowRight size={16} />
                  </Link>
                </Button>
                <Button asChild variant="ghost" data-testid="cta-explore">
                  <Link to="/about">Explore the University</Link>
                </Button>
              </div>

              {/* Credibility */}
              <div className="mt-10 flex flex-wrap items-center gap-5 reveal" data-delay="5">
                <div className="flex -space-x-2.5" data-testid="hero-credibility-avatars">
                  {[0, 1, 2, 3].map((i) => (
                    <Avatar key={i} size="md" seed={i + 12} initials={["A", "R", "M", "K"][i]} />
                  ))}
                </div>
                <div className="flex flex-col">
                  <span className="text-gold text-[14px] tracking-[0.2em]">★★★★★</span>
                  <span className="text-[13px] text-text-dim">
                    Trusted by <b className="text-text">25,000+ seekers</b> across{" "}
                    <b className="text-text">60+ countries</b>
                  </span>
                </div>
              </div>
            </div>

            {/* VISUAL */}
            <div className="relative aspect-square max-w-[560px] mx-auto w-full hidden lg:block reveal">
              {/* Orb */}
              <div
                className="absolute inset-[8%] rounded-full opacity-80"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, var(--plum-bright), var(--plum) 50%, transparent 75%)",
                  filter: "blur(28px)",
                }}
              />
              {/* Mandala layers */}
              <div className="absolute inset-0 grid place-items-center">
                <svg viewBox="0 0 200 200" fill="none" className="w-[88%] h-[88%]" aria-hidden="true">
                  <g stroke="var(--mauve)" strokeWidth="0.5" opacity="0.8">
                    <circle cx="100" cy="100" r="96" />
                    <circle cx="100" cy="100" r="78" />
                    <circle cx="100" cy="100" r="60" />
                    <path d="M100 4 L171 171 L29 171 Z M100 196 L29 29 L171 29 Z" />
                  </g>
                  <g
                    stroke="var(--neutral)"
                    strokeWidth="0.6"
                    opacity="0.5"
                    style={{ transformOrigin: "100px 100px", animation: "spin 60s linear infinite" }}
                  >
                    <circle cx="100" cy="100" r="96" />
                    <path d="M100 100 L100 6 M100 100 L166 34 M100 100 L194 100 M100 100 L166 166 M100 100 L100 194 M100 100 L34 166 M100 100 L6 100 M100 100 L34 34" />
                  </g>
                </svg>
              </div>

              {/* Central focal — ArtCard motif */}
              <div className="absolute inset-[24%] rounded-[20px] overflow-hidden shadow-float border border-line-2">
                <ArtCard seed={0} motifSeed={2} />
              </div>

              {/* Crest top-left */}
              <div className="absolute -top-2 left-2 flex items-center gap-2.5 px-3.5 py-2.5 rounded-full bg-glass-2 backdrop-blur-xl border border-line-2 shadow-float">
                <Award size={15} className="text-gold" />
                <span className="flex flex-col leading-tight">
                  <b className="text-[12px] font-semibold">Est. lineage</b>
                  <span className="text-[10px] text-text-faint font-mono uppercase tracking-[0.16em]">
                    Pramāṇa verified
                  </span>
                </span>
              </div>

              {/* Floating chip 1 */}
              <div className="absolute top-[14%] -right-2 flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-glass-2 backdrop-blur-xl border border-line-2 shadow-float">
                <BookOpen size={16} className="text-mauve" />
                <span className="flex flex-col leading-tight">
                  <b className="text-[12.5px] font-semibold">40,000+ Texts</b>
                  <span className="text-[10.5px] text-text-faint">Rare digital library</span>
                </span>
              </div>

              {/* Viz panel bottom-right */}
              <div className="absolute -bottom-2 right-4 px-3.5 py-3 rounded-2xl bg-glass-2 backdrop-blur-xl border border-line-2 shadow-float min-w-[200px]">
                <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.16em] text-text-faint mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
                  Consciousness Lab · live
                </div>
                <div className="flex items-end gap-0.5 h-6 mb-2">
                  {[6, 12, 9, 17, 11, 21, 14, 23, 16, 19, 13, 8].map((h, i) => (
                    <span
                      key={i}
                      className="flex-1 rounded-sm"
                      style={{
                        height: `${h * 3.5}%`,
                        background: "linear-gradient(180deg, var(--mauve), var(--plum))",
                        opacity: 0.6 + (i % 3) * 0.15,
                      }}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-text-dim">Coherence</span>
                  <b className="text-text font-semibold">0.92</b>
                </div>
              </div>

              {/* Sadhana chip bottom-left */}
              <div className="absolute -bottom-3 -left-2 flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-glass-2 backdrop-blur-xl border border-line-2 shadow-float">
                <Flame size={16} className="text-mauve" />
                <span className="flex flex-col leading-tight">
                  <b className="text-[12.5px] font-semibold">Advanced Sadhanas</b>
                  <span className="text-[10.5px] text-text-faint">Lineage-verified practice</span>
                </span>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-[22px] border border-line-2 bg-line reveal"
            data-testid="hero-stats"
          >
            {STATS.map((s, i) => (
              <div key={i} className="bg-bg p-6 lg:p-8" data-testid={`stat-${i}`}>
                <div
                  className="font-ui text-[clamp(32px,3.6vw,48px)] font-light leading-none tracking-[-0.035em] tabular-nums"
                  style={{
                    fontFeatureSettings: '"lnum" 1, "tnum" 1',
                    background: "linear-gradient(135deg, var(--neutral), var(--mauve))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {s.n}
                </div>
                <div className="mt-2 text-[12.5px] font-mono uppercase tracking-[0.16em] text-text-dim">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div
            className="mt-10 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 reveal"
            data-testid="hero-trust-strip"
          >
            {TRUST.map(({ Icon, t, s }, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <Icon size={16} className="text-mauve" />
                <span className="flex flex-col leading-tight">
                  <b className="text-[12.5px] font-semibold">{t}</b>
                  <span className="text-[10.5px] text-text-faint font-mono uppercase tracking-[0.14em]">
                    {s}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* HERO CAROUSEL — admin-managed banners for festivals / launches / promos */}
      <HeroCarousel />

      {/* FEATURED RAILS — driven by /api/featured + /api/homepage/sections */}
      <FeaturedRails />

      {/* DOWNLOADS — admin-uploaded brochures */}
      <HomeVideos />

      <DownloadsSection />

      {/* CTA Band */}
      <Section padded="sm" data-testid="home-cta-band">
        <Container>
          <div
            className="relative overflow-hidden rounded-[30px] border border-line-2 px-8 md:px-16 py-16 md:py-20 text-center reveal"
            style={{
              background:
                "radial-gradient(60% 80% at 50% 0%, var(--glow-plum), transparent 70%), var(--bg-elev)",
            }}
          >
            <Eyebrow centered>Begin your study</Eyebrow>
            <h2 className="font-display text-[clamp(34px,5vw,58px)] mt-4 leading-[1.05] tracking-[-0.01em]">
              The path of knowledge
              <br />
              leads to <AccentItalic>liberation</AccentItalic>.
            </h2>
            <p className="lede mx-auto mt-5">
              Join thousands of seekers worldwide. Get unlimited access to courses, the library,
              guided sadhanas, the research archive and live satsang — and begin today.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <Button asChild>
                <Link to="/courses">
                  Start Learning <ArrowRight size={16} />
                </Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/about">Talk to an Advisor</Link>
              </Button>
            </div>
          </div>
        </Container>
      </Section>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// Featured rails (fetched once from /api/featured). Wrapped here to keep the HomePage shell tidy.
function FeaturedRails() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/featured").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  if (!data) return null;
  return (
    <>
      <FeaturedRail eyebrow="Curriculum" title="Featured courses" cta={{ label: "View all courses", to: "/courses" }}
        items={data.courses || []} kind="course" base="/courses" priceKey="price_inr" />
      <FeaturedRail eyebrow="Practice" title="Sadhana programmes" cta={{ label: "All sadhana", to: "/sadhana" }}
        items={data.sadhana || []} kind="sadhana" base="/sadhana" priceKey="price_inr" />
      <FeaturedRail eyebrow="Sacred products" title="Lineage-verified objects" cta={{ label: "Shop products", to: "/products" }}
        items={data.products || []} kind="product" base="/products" priceKey="price_inr" />
      <FeaturedRail eyebrow="Consultation" title="Personal services" cta={{ label: "Browse services", to: "/services" }}
        items={data.services || []} kind="service" base="/services" priceKey="price_inr" />
      <FeaturedRail eyebrow="Scholarship" title="From the research center" cta={{ label: "All research", to: "/research" }}
        items={data.research || []} kind="research" base="/research" priceKey={null} />
      <FeaturedRail eyebrow="Reading" title="Digital library" cta={{ label: "Open the library", to: "/library" }}
        items={data.books || []} kind="book" base="/library" priceKey={null} />
    </>
  );
}

function FeaturedRail({ eyebrow, title, cta, items, kind, base, priceKey }) {
  const scrollerRef = useRef(null);
  if (!items?.length) return null;
  // Dedupe by id to suppress any duplicate-key warnings (defence in depth).
  const seen = new Set();
  const unique = items.filter((it) => { const k = it.id || it.slug; if (!k || seen.has(k)) return false; seen.add(k); return true; });
  if (!unique.length) return null;
  const scroll = (dir) => {
    const el = scrollerRef.current; if (!el) return;
    const delta = Math.round(el.clientWidth * 0.85) * (dir === "next" ? 1 : -1);
    el.scrollBy({ left: delta, behavior: "smooth" });
  };
  return (
    <Section padded="sm" data-testid={`home-rail-${kind}`}>
      <Container>
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="font-display text-[clamp(28px,3.4vw,42px)] mt-3 tracking-[-0.01em]">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {unique.length > 4 && (
              <>
                <button aria-label={`Previous ${kind}`} data-testid={`rail-prev-${kind}`} onClick={() => scroll("prev")}
                  className="w-10 h-10 rounded-full border border-line-2 bg-surface text-text-dim hover:text-mauve hover:border-mauve grid place-items-center transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <button aria-label={`Next ${kind}`} data-testid={`rail-next-${kind}`} onClick={() => scroll("next")}
                  className="w-10 h-10 rounded-full border border-line-2 bg-surface text-text-dim hover:text-mauve hover:border-mauve grid place-items-center transition-colors">
                  <ChevronRight size={16} />
                </button>
              </>
            )}
            {cta && (
              <Link to={cta.to} className="ml-1 text-[13.5px] text-mauve hover:underline inline-flex items-center gap-1"
                data-testid={`rail-cta-${kind}`}>
                {cta.label} <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
        <div ref={scrollerRef} data-testid={`rail-scroller-${kind}`}
          className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory -mx-4 px-4 pb-4 nvl-hide-scrollbar">
          {unique.map((it, i) => (
            <Link key={it.id || it.slug} to={`${base}/${it.slug}`} data-testid={`rail-${kind}-${it.slug}`}
              className="group block relative overflow-hidden rounded-[18px] border border-line-2 bg-surface aspect-[4/5] hover:-translate-y-1 transition-transform duration-300 flex-shrink-0 snap-start w-[calc(50%-8px)] sm:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]">
              {it.hero_image_url ? (
                <img src={it.hero_image_url} alt={it.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <ArtCard seed={i} motifSeed={i + 1} />
              )}
              <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end p-4 bg-gradient-to-t from-black/85 via-black/25 to-transparent" style={{ height: "55%" }}>
                {it.level && (
                  <span className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-neutral/80 mb-1">{it.level}</span>
                )}
                <h3 className="font-display text-[18px] tracking-[-0.01em] text-white leading-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">{it.title}</h3>
                {priceKey && it[priceKey] > 0 && (
                  <div className="text-[12.5px] text-neutral/90 mt-1.5 font-mono drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">₹ {Number(it[priceKey]).toLocaleString("en-IN")}</div>
                )}
                {it.journal && <div className="text-[10.5px] text-neutral/70 mt-1.5 font-mono uppercase tracking-[0.14em]">{it.journal}</div>}
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </Section>
  );
}

// ============================================================================
// HeroCarousel — admin-managed rotating banners (festivals / launches / promos)
// ============================================================================
function HeroCarousel() {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    api.get("/homepage/banners")
      .then((r) => setItems((r.data.items || []).filter((b) => b.image_url)))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!items || items.length < 2) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(timer.current);
  }, [items]);

  const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : apiBase + u);

  if (!items || items.length === 0) return null;
  const cur = items[idx];
  const go = (n) => setIdx((n + items.length) % items.length);

  return (
    <Section padded="none" className="pb-10" data-testid="home-hero-carousel">
      <Container>
        <div className="relative overflow-hidden rounded-[24px] border border-line-2 shadow-float aspect-[21/9] bg-surface">
          {items.map((b, i) => (
            <div key={b.id || i}
              className="absolute inset-0 transition-opacity duration-[900ms] ease-nvl"
              style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
              data-testid={`banner-slide-${i}`}>
              <img src={resolve(b.image_url)} alt={b.title || `banner-${i}`} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
              <div className="relative z-[2] h-full flex flex-col justify-end p-8 md:p-14 max-w-[720px]">
                {b.subtitle && <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-neutral/80 mb-3">{b.subtitle}</div>}
                {b.title && <h2 className="font-display text-[clamp(24px,3.6vw,44px)] text-white leading-tight">{b.title}</h2>}
                {b.link_url && (
                  <div className="mt-5">
                    <Button asChild data-testid={`banner-cta-${i}`}>
                      {b.link_url.startsWith("http") ? (
                        <a href={b.link_url} target="_blank" rel="noopener noreferrer">{b.cta_label || "Learn more"} <ArrowRight size={14} /></a>
                      ) : (
                        <Link to={b.link_url}>{b.cta_label || "Learn more"} <ArrowRight size={14} /></Link>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {items.length > 1 && (
            <>
              <button aria-label="Previous banner" onClick={() => go(idx - 1)} data-testid="banner-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-[3] w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center">
                <ChevronLeft size={18} />
              </button>
              <button aria-label="Next banner" onClick={() => go(idx + 1)} data-testid="banner-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-[3] w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center">
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[3] flex items-center gap-1.5" data-testid="banner-dots">
                {items.map((_, i) => (
                  <button key={i} aria-label={`Go to banner ${i + 1}`} onClick={() => setIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/50 hover:bg-white/80"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </Container>
    </Section>
  );
}

// ============================================================================
// DownloadsSection — brochure PDFs, admin-managed
// ============================================================================
function DownloadsSection() {
  const [items, setItems] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  useEffect(() => {
    api.get("/homepage/brochures").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const available = items.filter((b) => b.url);
  if (available.length === 0) return null;
  const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : apiBase + u);
  return (
    <Section padded="sm" data-testid="home-downloads">
      <Container>
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div>
            <Eyebrow>Downloads</Eyebrow>
            <h2 className="font-display text-[clamp(24px,2.8vw,34px)] mt-3 tracking-[-0.01em]">Brochures &amp; catalogues</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((b) => (
            <a key={b.slot} href={b.url ? resolve(b.url) : "#"} target={b.url ? "_blank" : undefined}
               rel="noopener noreferrer" download={b.url ? (b.name || "brochure.pdf") : undefined}
               className={`group flex flex-col p-5 rounded-2xl border border-line-2 bg-surface transition-all ${b.url ? "hover:border-mauve hover:-translate-y-0.5" : "opacity-60 pointer-events-none"}`}
               data-testid={`brochure-${b.slot}`}>
              <span className="w-11 h-11 rounded-xl border border-line-2 bg-glass grid place-items-center text-mauve mb-4">
                <FileText size={20} />
              </span>
              <span className="font-display text-[17px] leading-tight">{b.label}</span>
              <span className="text-[12px] text-text-faint mt-2">
                {b.url ? "PDF · Click to download" : "Not uploaded yet"}
              </span>
              {b.url && (
                <span className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] text-mauve group-hover:gap-2 transition-all">
                  <Download size={13} /> Download
                </span>
              )}
            </a>
          ))}
        </div>
      </Container>
    </Section>
  );
}
